import { utils, read, write } from 'xlsx';
import moment from 'moment';

// standard booking field ids are the same as the guest profile columns
const GUEST_PROFILE_KEYS = [
    'firstname',
    'lastname',
    'email',
    'phone',
    'city',
    'state',
    'country',
    'postalcode',
    'arrival_date',
    'departure_date'
];

// profile columns which are dates and have to be formatted for a 'date' booking field
const GUEST_PROFILE_DATE_KEYS = {
    arrivaldate: 'arrival_date',
    arrival: 'arrival_date',
    departuredate: 'departure_date',
    departure: 'departure_date'
};

const normalizeFieldName = s =>
    String(s ?? '')
        .trim()
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '');

const isEmptyValue = value => value === undefined || value === null || value === '';

/**
 * Collect the values we can prefill for the booker (or a guest) from the guest profile.
 *
 * @param customer guest profile
 * @param bookingFields booking_fields of the ticket(s) the panel renders, needed because
 *        client guest fields and ticket booking fields have separate id spaces, so anything
 *        which is not a standard field can only be matched by name
 */
export const getBookingGuestData = (customer, bookingFields = []) => {
    const guest = {};
    GUEST_PROFILE_KEYS.forEach(key => {
        if (customer[key]) {
            guest[key] = customer[key];
        }
    });

    const profileFields = customer.guest_field_info || [];

    bookingFields.forEach(field => {
        if (!isEmptyValue(guest[field.id])) {
            return;
        }

        // custom fields: match the saved guest field value by id, then by name
        const savedField =
            profileFields.find(f => f.id === field.id) ||
            profileFields.find(f => normalizeFieldName(f.name) === normalizeFieldName(field.name));
        if (savedField && !isEmptyValue(savedField.value)) {
            guest[field.id] = savedField.value;
            return;
        }

        // arrival / departure are profile columns, not guest_field_info entries
        const profileKey = GUEST_PROFILE_DATE_KEYS[normalizeFieldName(field.name)];
        if (profileKey && !isEmptyValue(customer[profileKey])) {
            guest[field.id] = customer[profileKey];
        }
    });

    // date fields are stored as YYYY-MM-DD strings by GuestExpansionPanel.
    // moment.utc, not moment: arrival_date / departure_date are DATE columns
    // serialised as UTC midnight, and reading them in local time moves the day
    // backwards everywhere west of UTC — Hawaii (UTC-10) included.
    bookingFields
        .filter(field => field.type === 'date' && !isEmptyValue(guest[field.id]))
        .forEach(field => {
            guest[field.id] = moment.utc(guest[field.id]).format('YYYY-MM-DD');
        });

    return guest;
};

/**
 * The booker-scope booking fields of a cart item's (or several items') tickets —
 * the set every prefill path has to look at, since booker values are collected
 * once for the whole order.
 *
 * @param tickets list of tickets
 */
export const bookerFieldsFromTickets = (tickets = []) =>
    tickets
        .map(ticket => ticket.booking_fields || [])
        .flat()
        .filter(field => field.bookerOnly === 'booker');

/**
 * Static defaults declared on the booker booking fields themselves
 * (`defaultValue`), applied whether or not we have a guest profile.
 *
 * @param tickets list of tickets
 */
export const getBookerFieldDefaults = (tickets = []) => {
    const bookerDefaults = {};
    bookerFieldsFromTickets(tickets).forEach(field => {
        if (!isEmptyValue(field.defaultValue)) {
            bookerDefaults[field.id] = field.defaultValue;
        }
    });
    return bookerDefaults;
};

/**
 * Prefill for the per person guest panels: only the stay dates, because name / email
 * of the profile belongs to the booker, not to every guest of the ticket.
 *
 * @param customer guest profile
 * @param bookingFields booking_fields rendered for that guest
 */
export const getStayDatePrefill = (customer, bookingFields = []) => {
    const prefill = {};
    bookingFields.forEach(field => {
        const profileKey =
            GUEST_PROFILE_DATE_KEYS[normalizeFieldName(field.name)] ||
            (['arrival_date', 'departure_date'].includes(field.id) ? field.id : null);
        if (!profileKey || isEmptyValue(customer[profileKey])) {
            return;
        }
        prefill[field.id] =
            field.type === 'date'
                ? moment.utc(customer[profileKey]).format('YYYY-MM-DD')
                : customer[profileKey];
    });
    return prefill;
};

/**
 * Prefill the arrival / departure booking fields of every guest of a cart item.
 *
 * Only empty slots are filled, so this is safe to re-run on a cart the guest has
 * already touched — which is what the ticket steppers on the review page do when
 * they add a ticket row or raise a quantity, and what the partner (JWT) hand-off
 * does on a cart that may still hold values.
 *
 * @param {Object} cartItem
 * @param {Object} customer guest profile (or null)
 * @returns {Object} cart item with the stay dates filled in
 */
export const withGuestStayDates = (cartItem, customer) => {
    if (!customer || !cartItem) {
        return cartItem;
    }
    return {
        ...cartItem,
        tickets: (cartItem.tickets || []).map(ticket => ({
            ...ticket,
            guests: (ticket.guests || []).map((guest, guestIndex) => {
                // 'item' fields are only rendered for the first guest of the ticket
                const fields = (ticket.booking_fields || []).filter(
                    ({ bookerOnly }) =>
                        bookerOnly === 'guest' || (guestIndex === 0 && bookerOnly === 'item')
                );
                const prefill = getStayDatePrefill(customer, fields);
                Object.keys(prefill).forEach(key => {
                    if (!isEmptyValue(guest?.[key])) {
                        delete prefill[key];
                    }
                });
                return { ...guest, ...prefill };
            })
        }))
    };
};

/**
 * Formats money amount display
 *
 * @param num amount
 */
export const formatMoney = num => {
    let parsedNum = num;
    if (typeof parsedNum !== 'number') {
        parsedNum = parseFloat(num);
        // cannot be parsed to float
        if (Number.isNaN(parsedNum)) {
            return '?';
        }
    }
    // 2 decimals, 3 long sections
    const re = `\\d(?=(\\d{${3}})+\\.)`;
    const ret = `$ ${parsedNum.toFixed(2).replace(new RegExp(re, 'g'), '$&,')}`;
    if (ret === '$ -0.00') {
        return '$ 0.00';
    }
    return ret;
};

// make a string header normalized for matching
const normalizeHeader = s =>
    String(s ?? '')
        .trim()
        .replace(/\s+/g, ' ')
        .toLowerCase();

// get the first value which is not undefined, null or empty string
const pickFirst = (...vals) => vals.find(v => v !== undefined && v !== null && v !== '');

// lookup builder for a row
const buildRowAccessor = row => {
    // build a normalized key-value map from the row
    const map = {};
    Object.keys(row || {}).forEach(k => {
        map[normalizeHeader(k)] = row[k];
    });

    // actual getter
    return keyOrKeys => {
        // keyOrKeys can be a single key or an array of keys
        const keys = Array.isArray(keyOrKeys) ? keyOrKeys : [keyOrKeys];
        // try to find the first matching key from the alias list and return its value
        for (const key of keys) {
            const value = map[normalizeHeader(key)];
            if (value !== undefined) {
                return value;
            }
        }
        // field not found
        return undefined;
    };
};

/**
 * Applies schema to rows of data
 * @param rows array of data rows
 * @param schema schema definition
 * @param options additional options
 * @returns transformed rows
 */
const applySchema = (rows, schema = {}, { keepUnknown = false } = {}) => {
    return rows.map(row => {
        // getter for the current row
        const get = buildRowAccessor(row);

        // output object
        const out = {};

        // process each schema rule
        for (const [outKey, rule] of Object.entries(schema)) {
            const {
                from = outKey,
                default: def = '',
                transform,
                type,
                format = 'YYYY-MM-DD',
                required = false
            } = rule || {};

            // if from is a string then it will be the key, if an array then takes the first existing key
            let value = get(from);

            // if value is undefined or empty or empty string then use default
            value = pickFirst(value, def);

            // if the type is a date then it formats it
            if (type === 'date') {
                if (value) {
                    const m = moment.utc(value);
                    value = m.isValid() ? m.format(format) : def;
                } else {
                    value = def;
                }
            }

            // apply transformation if any
            if (transform) {
                value = transform(value, row);
            }

            // check required fields
            if (required && (value === '' || value === null || value === undefined)) {
                throw new Error(`Missing required field: ${outKey}`);
            }

            out[outKey] = value;
        }

        // include unknown fields if option is set
        if (keepUnknown) {
            for (const [k, v] of Object.entries(row || {})) {
                if (!(k in out)) {
                    out[k] = v;
                }
            }
        }

        return out;
    });
};

/**
 * Parses XLSX file and returns JSON data from specified sheet
 * @param file uploaded file
 * @param cb callback on success
 * @param failCb callback on failure
 * @param options parsing options
 */
export const parseXlsxSheets = (file, cb, failCb = () => {}, options = {}) => {
    const {
        sheetIndex = 0,
        sheetName,
        parserOptions = { raw: false },
        schema,
        schemaOptions
    } = options;

    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(file);

    fileReader.onload = e => {
        try {
            const bufferArray = e?.target?.result;
            const wb = read(bufferArray, { type: 'array', cellDates: true });

            // if we have sheetname we use that, otherwise the index
            const chosenSheetName = sheetName ?? wb.SheetNames?.[sheetIndex];
            if (!chosenSheetName) {
                throw new Error('No sheet found in workbook.');
            }

            // get the worksheet and convert to json
            const ws = wb.Sheets[chosenSheetName];
            const data = utils.sheet_to_json(ws, parserOptions);

            // apply schema if any
            const result = schema ? applySchema(data, schema, schemaOptions) : data;

            cb(result);
        } catch (err) {
            console.error(err);
            failCb(err);
        }
    };

    fileReader.onerror = failCb;
};

export const toNDecimal = (num, decimals = 2) => {
    return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals);
};

/**
 * Formats date to MM/DD/YYYY format
 *
 * @param date date string or Date object
 * @returns formatted date string or '-' if invalid
 */
export const formatDate = date => {
    if (!date) {
        return '-';
    }
    const momentDate = moment(date);
    if (!momentDate.isValid()) {
        return '-';
    }
    return momentDate.format('MM/DD/YYYY');
};

/**
 * Formats date and time to MM/DD/YYYY hh:mm am/pm format
 *
 * @param date date string or Date object
 * @returns formatted date-time string or '-' if invalid
 */
export const formatDateTime = date => {
    if (!date) {
        return '-';
    }
    const momentDate = moment(date);
    if (!momentDate.isValid()) {
        return '-';
    }
    return momentDate.format('MM/DD/YYYY hh:mm A');
};

/**
 * Downloads example data as an XLSX file
 * @param data array of objects to export
 * @param filename name of the file to download
 */
export const downloadExampleXlsx = (data, filename = 'example.xlsx') => {
    const ws = utils.json_to_sheet(data);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, ws, 'Sheet1');

    // Generate buffer and create blob
    const wbout = write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/octet-stream' });

    // Create download link and trigger download
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
};

/**
 * JSON.parse that returns `fallback` instead of throwing on empty or malformed input
 */
export const safeJsonParse = (value, fallback = null) => {
    if (typeof value !== 'string') {
        return value ?? fallback;
    }
    try {
        return JSON.parse(value) ?? fallback;
    } catch (err) {
        return fallback;
    }
};
